'use client';

import {
  useCurrentUserInfo,
  useFetchCurrentUserJobsQuery,
  useFetchCurrentUserManagementJobsQuery,
  useFetchUserSettingQuery,
  useUpdateCareerMotivationMutation,
  useUpdateUserSettingMutation,
} from '@findy-code/yuffie/graphql';
import {
  CAREER_MOTIVATION_ID_CONSIDERING_CAREER,
  CAREER_MOTIVATION_ID_CURRENTLY_JOB_HUNTING,
  CAREER_MOTIVATION_ID_NO_IDEA,
  CAREER_MOTIVATION_ID_WANT_TO_HEAR,
  CAREER_MOTIVATION_ID_WITHIN_SIX_MONTHS,
  CAREER_MOTIVATION_ID_WITHIN_THREE_MONTHS,
} from '@findy-code/yuffie/models';
import { configService } from '@findy-code/yuffie/services/config';
import { UserActionTypes, userSelector } from '@findy-code/yuffie/store';
import { toast } from '@findy-code/yuffie/ui/toast';
import { getIsTargetUser } from '@findy-code/yuffie/utils/get-is-target-user';
import { getIsUsSupportUser } from '@findy-code/yuffie/utils/get-is-us-support-user';
import { startOfDay } from 'date-fns';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { FormData } from './types';

const USER_SUCCESS_MODAL = 'userSuccessModal';

const isValidCareerMotivationId = (id: number) => {
  return [
    CAREER_MOTIVATION_ID_CURRENTLY_JOB_HUNTING,
    CAREER_MOTIVATION_ID_NO_IDEA,
    CAREER_MOTIVATION_ID_WANT_TO_HEAR,
    CAREER_MOTIVATION_ID_WITHIN_SIX_MONTHS,
    CAREER_MOTIVATION_ID_WITHIN_THREE_MONTHS,
    process.env.NEXT_PUBLIC_FEATURE_ADD_CAREER_MOTIVATION_STATUS === 'true'
      ? CAREER_MOTIVATION_ID_CONSIDERING_CAREER
      : undefined,
  ]
    .filter((v) => v !== undefined)
    .includes(id);
};

export const useCareerMotivationModalFacade = () => {
  const dispatch = useDispatch();

  const { currentUser, isCurrentUserLoading } = useCurrentUserInfo();
  const { data: currentUserJobs, loading: isCurrentUserJobsLoading } =
    useFetchCurrentUserJobsQuery();
  const {
    data: currentUserManagementJobs,
    loading: isCurrentUserManagementJobsLoading,
  } = useFetchCurrentUserManagementJobsQuery();
  const { data: userSettings } = useFetchUserSettingQuery();

  // ユーザーの通知設定のいずれかが有効かどうか
  const isAnyLikeNotificationEnabled = useMemo(() => {
    return !!(
      userSettings?.currentUser.setting?.canReceiveImmediateLike ||
      userSettings?.currentUser.setting?.canReceiveWeeklyLikeRemind ||
      userSettings?.currentUser.setting?.canReceiveMonthlyLikeRemind ||
      userSettings?.currentUser.setting?.canReceiveExpiredLikeRemind
    );
  }, [userSettings?.currentUser.setting]);

  // 転職意欲更新のmutationに失敗したかどうか
  const isUpdateCareerMotivationErrorRef = useRef(false);

  const [updateUserSetting] = useUpdateUserSettingMutation({
    onCompleted: async (data) => {
      if (data.updateUserSetting?.userSetting.id === undefined) {
        toast.error('いいねの受け取り設定を更新できませんでした');
      }
    },
    onError: () => {
      toast.error('いいねの受け取り設定を更新できませんでした');
    },
  });

  const [
    _updateCareerMotivation,
    { loading: isUpdateCareerMotivationLoading },
  ] = useUpdateCareerMotivationMutation({
    onCompleted: async (data) => {
      if (!data.updateCareerMotivation?.user.profile) {
        isUpdateCareerMotivationErrorRef.current = true;
        toast.error('転職意欲の更新に失敗しました');
        return;
      }

      const careerMotivationId =
        data.updateCareerMotivation.user.profile.careerMotivation;

      dispatch({
        type: UserActionTypes.UPDATE_USER_CAREER_MOTIVATION,
        payload: {
          career_motivation: {
            user_answer_id: careerMotivationId,
          },
        },
      });

      if (
        !isAnyLikeNotificationEnabled &&
        careerMotivationId !== CAREER_MOTIVATION_ID_NO_IDEA
      ) {
        if (
          window.confirm(
            '現在の設定では企業からのいいねの通知が受け取れない状態になっています。いいねの通知を受け取れる状態に変更しますか？'
          )
        ) {
          await updateUserSetting({
            variables: {
              input: {
                canReceiveImmediateLike: true,
              },
            },
          });
        }
      }
    },

    onError: () => {
      isUpdateCareerMotivationErrorRef.current = true;
      toast.error('転職意欲の更新に失敗しました');
    },
  });

  const updateCareerMotivation = useCallback(
    async ({ careerMotivation }: FormData) => {
      const id = parseInt(careerMotivation);

      if (!isValidCareerMotivationId(id)) {
        return;
      }

      await _updateCareerMotivation({
        variables: {
          input: {
            careerMotivationId: id,
          },
        },
      });

      if (isUpdateCareerMotivationErrorRef.current) {
        return;
      }
    },
    [_updateCareerMotivation]
  );

  const { career_motivation: currentCareerMotivation } =
    useSelector(userSelector);
  const values: FormData = {
    careerMotivation: `${
      currentCareerMotivation?.user_answer_id &&
      isValidCareerMotivationId(currentCareerMotivation.user_answer_id)
        ? currentCareerMotivation.user_answer_id
        : ''
    }`,
  };

  /** @internal */
  const reloadIfUpdateSucceeded = useCallback(() => {
    if (isUpdateCareerMotivationErrorRef.current) {
      return;
    }
    window.location.reload();
  }, []);

  // Modal内のReactModalに渡すisOpenの値。
  // 現状、reduxを経由した仕組みでモーダルが管理されているため、齟齬がないように初期値をtrueにしている。
  const [isOpenCareerMotivationModal, setIsOpenCareerMotivationModal] =
    useState(true);

  /** @internal */
  const closeCareerMotivationModal = useCallback(() => {
    setIsOpenCareerMotivationModal(false);
  }, []);

  // 以降はUS面談モーダルを管理するための機能
  // FIXME: 可能であればUS面談モーダルの管理を、CareerMotivationModalの外に移動したい
  const [isOpenUsSuccessModal, setIsOpenUsSuccessModal] = useState(false);
  const resolverRef = useRef<((value?: unknown) => void) | null>(null);

  /**
   * US面談モーダルを表示し、閉じるまで待機するためのPromiseを返す。
   * Promiseはモーダルが閉じる時にresolveされる。
   * @internal
   */
  const openUserSuccessModal = useCallback(async () => {
    setIsOpenUsSuccessModal(true);
    return new Promise<unknown>((resolve) => {
      resolverRef.current = resolve;
    });
  }, [setIsOpenUsSuccessModal]);

  const closeUserSuccessModal = useCallback(() => {
    setIsOpenUsSuccessModal(false);
    if (resolverRef.current !== null) {
      resolverRef.current();
    }
  }, []);

  /**
   * ユーザーの転職意欲を更新した後に表示するモーダルを表示するかどうかを判定する
   * @internal
   */
  const shouldShowUserSuccessModal = useCallback(
    ({ careerMotivation }: FormData) => {
      const careerMotivationId = Number.parseInt(careerMotivation);
      const lastShownAt = configService.getLastShownAt(USER_SUCCESS_MODAL);

      // 空文字の場合は初回表示として扱う
      const isFirstTime = lastShownAt === '';

      const current = startOfDay(new Date());
      const comparedDate = startOfDay(Date.parse(lastShownAt));

      const IsUsSupportUser =
        process.env.NEXT_PUBLIC_FEATURE_TU_API_REPLACEMENT === 'true'
          ? getIsUsSupportUser({
              isTu: currentUser?.isTu,
              talentStatus: currentUser?.talentStatus,
            })
          : getIsTargetUser({
              age: currentUser?.profile?.age,
              numericAnnualIncome: currentUser?.profile?.numericAnnualIncome,
              prefectureId: currentUser?.profile?.prefecture.id,
              talentStatus: currentUser?.talentStatus,
              jobTypeIds: currentUserJobs?.currentUser?.jobs.map(
                (job) => job.jobTypeId
              ),
              managementJobTypeIds:
                currentUserManagementJobs?.currentUser.managementJobs.map(
                  (job) => job.managementJobTypeId
                ),
              userJobYearTypeIds: currentUserJobs?.currentUser?.jobs.map(
                (job) => job.userJobYearTypeId
              ),
              userManagementJobYearTypeIds:
                currentUserManagementJobs?.currentUser.managementJobs.map(
                  (job) => job.userJobYearTypeId
                ),
            });

      return (
        IsUsSupportUser &&
        (isFirstTime || current > comparedDate) &&
        isValidCareerMotivationId(careerMotivationId) &&
        careerMotivationId !== CAREER_MOTIVATION_ID_NO_IDEA
      );
    },
    [currentUser, currentUserJobs, currentUserManagementJobs]
  );

  const handleAfterUpdateCareerMotivation = useCallback(
    async (values: FormData) => {
      closeCareerMotivationModal();
      if (shouldShowUserSuccessModal(values)) {
        await openUserSuccessModal();
      }
      reloadIfUpdateSucceeded();
    },
    [
      closeCareerMotivationModal,
      shouldShowUserSuccessModal,
      openUserSuccessModal,
      reloadIfUpdateSucceeded,
    ]
  );

  return {
    isOpenUsSuccessModal,
    isOpenCareerMotivationModal,
    closeCareerMotivationModal,
    openUserSuccessModal,
    closeUserSuccessModal,
    isLoading:
      isUpdateCareerMotivationLoading ||
      isCurrentUserLoading ||
      isCurrentUserJobsLoading ||
      isCurrentUserManagementJobsLoading,
    updateCareerMotivation,
    shouldShowUserSuccessModal,
    reloadIfUpdateSucceeded,
    handleAfterUpdateCareerMotivation,
    values,
  } as const;
};
